import { useTranslation } from 'react-i18next'

import { GroupFilter } from './GroupFilter'
import { ObjectFilter } from './ObjectFilter'
import { StatusFilter } from './StatusFilter'
import { TypeFilter } from './TypeFilter'
import { DateRangeDropdown } from '@/Atomic/Molecules/DateRangeDropdown'
import { useFilterValues } from '@/Components/FormContext'
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter'
import { getActiveAttributeFilterCount } from '@/utils/filterParams'

import './FiltersDropdown.scss'

interface DateRangeFilterConfig {
  fieldPrefix?: string
}

interface StatusFilterConfig {
  dataClassName?: string
  filterKey?: string
}

interface TypeFilterConfig {
  dataClassName?: string
  filterKey?: string
}

interface FiltersDropdownProps {
  showStatusFilter?: boolean
  statusFilterConfig?: StatusFilterConfig
  showTypeFilter?: boolean
  typeFilterConfig?: TypeFilterConfig
  showGroupFilter?: boolean
  showDateRangeFilter?: boolean
  dateRangeFilterConfig?: DateRangeFilterConfig
  showObjectFilter?: boolean
  onClearAll?: () => void
  onClose?: () => void
}

export const FiltersDropdown: React.FC<FiltersDropdownProps> = ({
  showStatusFilter,
  statusFilterConfig,
  showTypeFilter,
  typeFilterConfig,
  showGroupFilter,
  showDateRangeFilter,
  dateRangeFilterConfig,
  showObjectFilter,
  onClearAll,
  onClose,
}) => {
  const { t } = useTranslation()
  const { values } = useFilterValues()
  const dateRange = useDateRangeFilter(dateRangeFilterConfig?.fieldPrefix)

  // The design shows "Clear all filters" only while there is something to clear,
  // and the same for the per-field "Clear" links below (CRM-1272).
  const hasActiveFilters = getActiveAttributeFilterCount(values) > 0

  const handleClearMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const renderClearButton = (onClear: () => void) => (
    <button
      className="filters-dropdown__clear-button"
      onMouseDown={handleClearMouseDown}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onClear()
        onClose?.()
      }}
      type="button"
    >
      {t('common.clear')}
    </button>
  )

  return (
    <div className="filters-dropdown">
      <div className="filters-dropdown__header">
        <h3 className="filters-dropdown__title">{t('common.filters')}</h3>
        {hasActiveFilters && (
          <button
            className="filters-dropdown__clear-all-button"
            onMouseDown={handleClearMouseDown}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onClearAll?.()
              onClose?.()
            }}
            type="button"
          >
            {t('common.filter.clearAll')}
          </button>
        )}
      </div>
      <div className="filters-dropdown__content">
        {showStatusFilter && (
          <div className="filters-dropdown__field">
            <StatusFilter
              {...statusFilterConfig}
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="filters-dropdown__label-wrapper">
                  <label className="filters-dropdown__label">
                    {t('common.filter.status')}
                  </label>
                  {hasValue && renderClearButton(onClear)}
                </div>
              )}
            />
          </div>
        )}
        {showObjectFilter && (
          <div className="filters-dropdown__field">
            <ObjectFilter
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="filters-dropdown__label-wrapper">
                  <label className="filters-dropdown__label">
                    {t('tables.headers.object')}
                  </label>
                  {hasValue && renderClearButton(onClear)}
                </div>
              )}
            />
          </div>
        )}
        {showTypeFilter && (
          <div className="filters-dropdown__field">
            <TypeFilter
              {...typeFilterConfig}
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="filters-dropdown__label-wrapper">
                  <label className="filters-dropdown__label">
                    {t('common.filter.type')}
                  </label>
                  {hasValue && renderClearButton(onClear)}
                </div>
              )}
            />
          </div>
        )}
        {showGroupFilter && (
          <div className="filters-dropdown__field">
            <GroupFilter
              renderLabel={(hasValue: boolean, onClear: () => void) => (
                <div className="filters-dropdown__label-wrapper">
                  <label className="filters-dropdown__label">
                    {t('common.filter.group')}
                  </label>
                  {hasValue && renderClearButton(onClear)}
                </div>
              )}
            />
          </div>
        )}
        {showDateRangeFilter && (
          <div className="filters-dropdown__field">
            <div className="filters-dropdown__label-wrapper">
              <label className="filters-dropdown__label">
                {t('common.filter.dateRange')}
              </label>
              {!!(dateRange.value[0] && dateRange.value[1]) &&
                renderClearButton(() => dateRange.onChange([null, null]))}
            </div>
            <DateRangeDropdown
              presets={dateRange.presets}
              value={dateRange.value}
              onChange={dateRange.onChange}
              triggerClassName="filter-button"
            />
          </div>
        )}
      </div>
    </div>
  )
}
